import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Stack } from '@figma/fpl-components';
import cssVars from '@figma/fpl-tokens/css-vars';
import cloneDeep from 'lodash/cloneDeep';
import noop from 'lodash/noop';
import set from 'lodash/set';
import { zIndex as fplZIndex } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { RunErrorBanner } from '@/UI/RunErrorBanner/RunErrorBanner';
import { TextArea } from '@/UI/TextArea/TextArea';
import { TextSmRg, TextStdMd } from '@/UI/AppTypography/AppTypography';
import { FlexCol } from '@/UI/styles';
import { color } from '@/colors';
import { log } from '@/logger/logger.ts';
import { I18N_KEYS } from '@/language/keys';
import { NodeType } from '@/enums/node-type.enum';
import { DesignAppMode } from '@/enums/design-app-modes.enum';
import { RunType } from '@/enums/run-type.enum';
import { ZIndex } from '@/consts/z-indexes.consts';
import { getSignInReturnPath } from '@/utils/urls';
import { TOOL_RUN_EVENT } from '@/consts/analytics.consts';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { CreditsContext } from '@/services/CreditsContext';
import { useRunFlow } from '@/hooks/useToolRun';
import { renderDesignAppParams } from '@/components/Recipe/DesignApp/DesignAppUtils';
import DesignAppInputHeader, {
  IteratorDescription,
  requiredLabel,
} from '@/components/Recipe/DesignApp/DesignAppInputHeader';
import { blocksRunWhenEmpty } from '@/components/Recipe/DesignApp/toolRequiredInputs';
import { hasUsableInputValue } from '@/utils/toolInputValue';
import { initialToolInputValue } from '@/utils/toolInputInitialValue';
import DesignAppPrompt from '@/components/Recipe/DesignApp/DesignAppPrompt';
import DesignAppUploadFile from '@/components/Recipe/DesignApp/DesignAppUploadFile';
import DesignAppMediaIterator from '@/components/Recipe/DesignApp/DesignAppMediaIterator';
import DesignAppTextIterator from '@/components/Recipe/DesignApp/DesignAppTextIterator';
import DesignAppIteratorsSummary from '@/components/Recipe/DesignApp/DesignAppIteratorsSummary';
import Preview from '@/components/Recipe/DesignApp/Preview/Preview';
import CancellableLoadingButton from '@/components/CancellableLoadingButton/CancellableLoadingButton';
import { PriceInfo } from '@/components/Common/PriceInfo/PriceInfo.tsx';
import { FileUploader } from '@/components/Common/FileUploader/ToolsView/FileUploader';
import { RunCounter } from '@/components/Recipe/Drawer/RunModelsSection.tsx';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import { saveToolUserData } from '@/api/toolRunData.api';
import { designAppParamsFromViewer } from '@/components/Recipe/DesignApp/designAppParams';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_WORKFLOW_APP_BUILDER_MODE } from '@/consts/featureFlags';
import {
  ToolArrayInput,
  ToolBooleanInput,
  ToolDropdownInput,
  ToolInputRow,
  ToolNumberInput,
  ToolSeedInput,
  ToolTextInput,
} from './ToolInputControls';
import { ToolLoraInput, type ToolLoraValue } from './ToolLoraInput';
import { ToolMediaIterator, ToolTextIterator } from './ToolIterators';
import { ToolColorPaletteInput } from './ToolColorPaletteInput';
import { toolOutputExamples } from './ToolOutputExamples';
import { ToolMainView } from './ToolMainView';
import {
  MAIN_CONTENT_MIN_WIDTH,
  PANEL_RESIZER_WIDTH,
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  panelStyles,
  sidebarVisibilityStyles,
} from './ToolLayout.stylex';
import type { RunEndpoints } from '@/api/toolRunEndpoints';
import type { ToolViewMetadata } from '@/api/toolView.api';
import type { ToolInput, ToolLoraOption, ToolRunData } from '@/api/toolRunData.api';
import type { DesignAppData, DesignAppInput, DesignAppResult, MediaIteratorInput } from '@/types/design-app.types';
import type { FileType } from '@/hooks/useUploadMediaAsset';
import type { RecipeRunCostRequestBody } from '@/api/recipe-runs/recipe-runs.api.types';
import type { MediaAsset } from '@/types/api/assets';

const logger = log.getLogger('ToolViewer');

// The reused DesignApp leaf components read a node's { id, type, data }; the graph-less viewer
// synthesizes that minimal shape from the run-data descriptors.
type ViewerNode = {
  id: string;
  type: NodeType;
  data: {
    name: string;
    description: string;
    min?: number;
    max?: number;
    acceptedFileType?: FileType;
    loras?: ToolLoraOption[];
  };
};

type ViewerInput = DesignAppInput & {
  values?: (string | number)[];
  loraMode?: ToolInput['loraMode'];
  loraOptions?: ToolInput['loraOptions'];
};

// A selector the user hasn't touched holds inspect's default, which is the option itself rather than
// its index.
const selectorIndex = (input: ViewerInput): number => {
  const index = typeof input.value === 'string' ? (input.values ?? []).indexOf(input.value) : -1;
  return index >= 0 ? index : Number(input.value ?? 0);
};

// The legacy dropdown's options are indexes. Map only for display, so a selector nobody picked keeps
// the option itself, which the run and the redesign already read.
const withSelectorIndex = (input: ViewerInput): ViewerInput =>
  isSelector(input) ? ({ ...input, value: selectorIndex(input) } as unknown as ViewerInput) : input;

const isSelector = (input: ViewerInput): boolean => input.type === NodeType.MuxV2 && !input.isIterator;
const isTextIterator = (input: ViewerInput): boolean => input.type === NodeType.MuxV2 && !!input.isIterator;

function buildInitialInputs(
  runData: ToolRunData,
  userData: DesignAppData | null,
  isToolViewRedesignEnabled: boolean,
): ViewerInput[] {
  const overrides = new Map((userData?.inputs ?? []).map((i) => [i.id, i]));
  // Flatten outputs[].inputs, de-duping by nodeId (an input can feed multiple outputs).
  const seen = new Set<string>();
  const descriptors: ToolInput[] = [];
  runData.outputs.forEach((output) => {
    output.inputs.forEach((input) => {
      if (seen.has(input.nodeId)) return;
      seen.add(input.nodeId);
      descriptors.push(input);
    });
  });

  return descriptors
    .map((descriptor): ViewerInput => {
      const override = overrides.get(descriptor.nodeId);
      const isIterator = descriptor.type === NodeType.MuxV2 && !!descriptor.isIterator;
      let value: unknown = initialToolInputValue(descriptor, override);
      // The legacy LoRA control had an Enabled/Disabled switch that kept its selection.
      // Restore that saved off state; the redesigned picker represents it as None.
      let disabled = descriptor.type === NodeType.MultiLora ? (override?.disabled ?? false) : false;
      if (
        isToolViewRedesignEnabled &&
        descriptor.type === NodeType.MultiLora &&
        descriptor.loraMode !== 'single' &&
        descriptor.loraOptions
      ) {
        const loraValue = value as { selectedLora?: ToolLoraOption | null; weight?: number | string } | undefined;
        // An empty or removed selection starts as None in both the control and run payload.
        const selectedId = loraValue?.selectedLora?.id;
        disabled ||= !descriptor.loraOptions.some((option) => option.id === selectedId);
        // The legacy weight field saves typed numbers as strings.
        const weight = loraValue?.weight;
        const numericWeight = typeof weight === 'string' && weight.trim() !== '' ? Number(weight) : weight;
        value = {
          ...loraValue,
          selectedLora: disabled ? null : (loraValue?.selectedLora ?? null),
          weight: typeof numericWeight === 'number' && Number.isFinite(numericWeight) ? numericWeight : 0.5,
        };
      }
      return {
        id: descriptor.nodeId,
        type: descriptor.type,
        order: descriptor.order,
        value,
        exposed: descriptor.exposed,
        disabled,
        required: descriptor.required ?? false,
        description: descriptor.description ?? null,
        values: descriptor.values,
        loraMode: descriptor.loraMode,
        loraOptions: descriptor.loraOptions,
        ...(isIterator ? { isIterator: true } : {}),
      } as ViewerInput;
    })
    .sort((a, b) => a.order - b.order);
}

function buildViewerNodes(runData: ToolRunData): ViewerNode[] {
  const seen = new Set<string>();
  const nodes: ViewerNode[] = [];
  runData.outputs.forEach((output) => {
    output.inputs.forEach((input) => {
      if (seen.has(input.nodeId)) return;
      seen.add(input.nodeId);
      nodes.push({
        id: input.nodeId,
        type: input.type,
        data: {
          name: input.name ?? '',
          description: input.description ?? '',
          min: input.numberRange?.min,
          max: input.numberRange?.max,
          acceptedFileType:
            input.acceptedFileType ??
            (output.outputType === 'video' && input.type === NodeType.MediaIterator ? 'video' : 'image'),
          // LoraSelect reads its choices from node.data.loras, as it does for a real MultiLora node.
          loras: input.loraOptions,
        },
      });
    });
  });
  return nodes;
}

interface ToolViewerProps {
  toolId?: string; // unpublished tools do not have an id
  endpoints: RunEndpoints;
  runData: ToolRunData;
  userData: DesignAppData | null;
  /** Preview input edits stay local instead of being saved as tool user data. */
  isPreviewMode?: boolean;
  toolName: string;
  metadata?: ToolViewMetadata;
  /** No signed-in user — cost/run are gated behind sign-in (mirrors prod DesignApp). */
  isGuest: boolean;
  /** For the run event only — the viewer is read-only for the owner too. Undefined if the host can't tell. */
  isOwner?: boolean;
  isSidebarOpen?: boolean;
  hideSidebarOnMobile?: boolean;
  isToolViewRedesignEnabled?: boolean;
}

// Builds its form purely from the run-data descriptors (no nodes / WASM / Flow), reusing the prod
// DesignApp leaf components; callers can supply workflow or published-tool endpoints.
export function ToolViewer({
  toolId,
  endpoints,
  runData,
  userData,
  isPreviewMode = false,
  toolName,
  metadata,
  isGuest,
  isOwner,
  isSidebarOpen = true,
  hideSidebarOnMobile = false,
  isToolViewRedesignEnabled = false,
}: ToolViewerProps) {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const { value: isWorkflowAppBuilderEnabled } = useFeatureFlag(FF_WORKFLOW_APP_BUILDER_MODE);
  // A tool config is what makes `required` mean Mandatory; without one every input stays required.
  const optionalInputsCanRunEmpty = Boolean(isWorkflowAppBuilderEnabled) && runData.hasToolConfig === true;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const viewerNodes = useMemo(() => buildViewerNodes(runData), [runData]);
  const examples = useMemo(
    () => toolOutputExamples(runData.outputs, runData.resolvedAssets ?? {}),
    [runData.outputs, runData.resolvedAssets],
  );
  const [inputs, setInputs] = useState<ViewerInput[]>(() =>
    buildInitialInputs(runData, userData, isToolViewRedesignEnabled),
  );
  const [results, setResults] = useState<DesignAppResult[]>(userData?.results ?? []);
  const [numberOfRuns, setNumberOfRuns] = useState(1);
  const [toolUploads, setToolUploads] = useState({ activeBatches: 0, attemptedRun: false });
  const handleUploadBatchChange = useCallback((uploading: boolean) => {
    setToolUploads((current) => {
      const activeBatches = current.activeBatches + (uploading ? 1 : -1);
      return { activeBatches, attemptedRun: activeBatches > 0 && current.attemptedRun };
    });
  }, []);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [leftPanelWidth, setLeftPanelWidth] = useState(`${localStorage.getItem('designAppLeftPanelWidth') || '30'}%`);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const savedWidth = Number(localStorage.getItem('toolSidebarWidthPx'));
    return Number.isFinite(savedWidth) && savedWidth >= SIDEBAR_MIN_WIDTH && savedWidth <= SIDEBAR_MAX_WIDTH
      ? savedWidth
      : SIDEBAR_MIN_WIDTH;
  });
  const [isDraggingPanel, setIsDraggingPanel] = useState(false);

  const { setUserCredits, setWorkspaceCredits } = useContext(CreditsContext);
  const { openConfirmationDialog } = useConfirmationDialogStore();

  const inputRefs = useRef<Record<string, HTMLElement>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const inputsContainerRef = useRef<HTMLDivElement>(null);

  const mode = DesignAppMode.Running;
  const role = 'guest';

  // Keep every input for run/cost defaults; only exposed, editable ones render as parameters.
  const exposedInputs = useMemo(
    () =>
      inputs.filter(
        (input) =>
          input.exposed &&
          !(isToolViewRedesignEnabled && input.type === NodeType.MultiLora && input.loraMode === 'single'),
      ),
    [inputs, isToolViewRedesignEnabled],
  );

  const updateResults = useCallback(
    (newResults: DesignAppResult[], userRemainingCredits?: number, workspaceRemainingCredits?: number) => {
      try {
        if (!newResults) throw new Error('No results received');
        setResults((prev) => {
          const sorted = [...newResults].sort((a, b) => (a.inputIndex ?? 0) - (b.inputIndex ?? 0));
          return [...sorted, ...(prev ?? [])];
        });
        if (userRemainingCredits !== undefined) setUserCredits(userRemainingCredits);
        if (workspaceRemainingCredits !== undefined) setWorkspaceCredits(workspaceRemainingCredits);
      } catch (error) {
        logger.error('Failed to update results', { error });
      }
    },
    [setUserCredits, setWorkspaceCredits],
  );

  const iteratorInputs = useMemo(
    () => exposedInputs.filter((input) => isTextIterator(input) || input.type === NodeType.MediaIterator),
    [exposedInputs],
  );

  const iteratorSummary = useMemo(
    () =>
      iteratorInputs.map((input) => {
        const node = viewerNodes.find((n) => n.id === input.id);
        const value = input.value as { files?: unknown[]; options?: unknown[] } | undefined;
        const valueCount =
          input.type === NodeType.MediaIterator ? (value?.files?.length ?? 0) : (value?.options?.length ?? 0);
        return { id: input.id, name: node?.data.name ?? input.id, valueCount };
      }),
    [iteratorInputs, viewerNodes],
  );

  const getRunBody = useCallback(
    (runs: number): RecipeRunCostRequestBody => ({
      inputs: inputs.map((input) => ({
        nodeId: input.id,
        input: input.value,
        disabled: input.disabled,
        name: viewerNodes.find((n) => n.id === input.id)?.data.name,
      })),
      numberOfRuns: runs,
      recipeVersion: runData.recipeVersion,
    }),
    [inputs, viewerNodes, runData.recipeVersion],
  );

  const validateInputs = useCallback(() => {
    const errors: Record<string, string> = {};
    exposedInputs
      .filter((input) => !input.disabled && (!optionalInputsCanRunEmpty || blocksRunWhenEmpty(input)))
      .forEach((input) => {
        const value = input.value as Record<string, unknown> | undefined;
        if (
          value == null ||
          ((input.type === NodeType.Prompt || input.type === NodeType.PromptV3) &&
            !(value as { prompt?: string }).prompt) ||
          (input.type === NodeType.String && !(value as { string?: string }).string?.trim())
        ) {
          errors[input.id] = t(I18N_KEYS.SHARED_DESIGN_APP.INPUTS.REQUIRED_ERROR);
        }
        const fileVal = value as { file?: { url?: string }; url?: string } | undefined;
        if (input.type === NodeType.Import && !fileVal?.file?.url && !fileVal?.url) {
          errors[input.id] = t(I18N_KEYS.SHARED_DESIGN_APP.INPUTS.REQUIRED_ERROR_FILE);
        }
        const files = (value as { files?: { url?: string }[] } | undefined)?.files;
        if (input.type === NodeType.MediaIterator && (!files?.length || !files.some((f) => f.url))) {
          errors[input.id] = t(I18N_KEYS.SHARED_DESIGN_APP.INPUTS.REQUIRED_ERROR_MEDIA_ITERATOR);
        }
        const options = (value as { options?: string[] } | undefined)?.options;
        if (isTextIterator(input) && (!options || !options.some((o) => o.trim()))) {
          errors[input.id] = t(I18N_KEYS.SHARED_DESIGN_APP.INPUTS.REQUIRED_ERROR_TEXT_ITERATOR);
        }
        // Catches what core would refuse that the checks above let through, e.g. a blank prompt.
        if (!errors[input.id] && input.required && !hasUsableInputValue(input, input.value)) {
          errors[input.id] = t(I18N_KEYS.SHARED_DESIGN_APP.INPUTS.REQUIRED_ERROR);
        }
      });

    // Core still checks required values when the input is disabled (e.g. a LoRA set to None).
    exposedInputs
      .filter((input) => input.disabled && input.required && !hasUsableInputValue(input, input.value))
      .forEach((input) => {
        errors[input.id] = t(I18N_KEYS.SHARED_DESIGN_APP.INPUTS.REQUIRED_ERROR);
      });

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstErrorId = Object.keys(errors)[0];
      const container = inputsContainerRef.current;
      const errorElement = inputRefs.current[firstErrorId];
      if (container && errorElement) {
        container.scrollTo({ top: errorElement.offsetTop - container.offsetTop - 16, behavior: 'smooth' });
      }
    }
    return Object.keys(errors).length === 0;
  }, [exposedInputs, optionalInputsCanRunEmpty, t]);

  const {
    runCost,
    isDynamicCost,
    isLoadingCost,
    isRunningApp,
    errorMessage,
    setErrorMessage,
    isProcessing,
    progress,
    pollingErrorMessage,
    numberOfLoadingResults,
    canCancel,
    startPolling,
    fetchRunCost,
    run,
    cancelRun,
  } = useRunFlow({
    endpoints,
    isGuest,
    getRunBody,
    onResults: updateResults,
    validateInputs,
  });
  const isRunActive = isRunningApp || isProcessing;
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    // reset cancelling state when the run is no longer active
    if (!isRunActive) setIsCancelling(false);
  }, [isRunActive]);

  /** Credits this click is quoted, as shown beside the Run button. */
  const totalRunCost = runCost != null ? Math.round(runCost * Math.floor(numberOfRuns) * 100) / 100 : undefined;

  // Resume polling any runs that were in-flight when the user last left.
  useEffect(() => {
    if (userData?.latestRuns?.length) {
      startPolling(userData.latestRuns.map((latestRun) => latestRun.runId));
      canCancel.current = true;
    }
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, []);

  const costDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const inputsFingerprint = useMemo(
    () =>
      inputs
        .map((i) => {
          switch (i.type) {
            case NodeType.Import:
            case NodeType.MultiLora:
              return null;
            case NodeType.MediaIterator:
              return `${i.id}:files:${(i.value as { files?: unknown[] })?.files?.length ?? 0}`;
            default:
              return `${i.id}:${JSON.stringify(i.value)}`;
          }
        })
        .filter(Boolean)
        .join('|'),
    [inputs],
  );

  useEffect(() => {
    clearTimeout(costDebounceRef.current);
    costDebounceRef.current = setTimeout(() => void fetchRunCost(1), 500);
    return () => clearTimeout(costDebounceRef.current);
  }, [inputsFingerprint, fetchRunCost]);

  const saveDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Never persist the initial seed: if user-data failed to load, the seed is defaults and would
  // clobber the user's saved values. Only edits (which flip this) are saved. Descriptors are read
  // when that save fires, so a new outputs array cannot write the seed on its own.
  const seededRef = useRef(true);
  const outputsRef = useRef(runData.outputs);
  outputsRef.current = runData.outputs;
  useEffect(() => {
    if (isGuest || isPreviewMode || !toolId) return;
    if (seededRef.current) {
      seededRef.current = false;
      return;
    }
    clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = setTimeout(() => {
      const seen = new Set<string>();
      const descriptors = outputsRef.current.flatMap((output) =>
        output.inputs.filter((input) => {
          if (seen.has(input.nodeId)) return false;
          seen.add(input.nodeId);
          return true;
        }),
      );
      void saveToolUserData(toolId, designAppParamsFromViewer(inputs, descriptors)).catch((error) =>
        logger.error('Failed to save tool user data', error),
      );
    }, 500);
    return () => clearTimeout(saveDebounceRef.current);
  }, [inputs, isGuest, isPreviewMode, toolId]);

  const updateInput = (inputId: string, field: string, newValue: unknown) => {
    setInputs((prev) =>
      prev.map((input) => {
        if (input.id !== inputId) return input;
        return set(cloneDeep(input), field, newValue);
      }),
    );
  };

  const handleChange = (paramId: string, newValue: unknown) => {
    if (isGuest) return;
    setErrorMessage(undefined);
    setValidationErrors((prev) => {
      const updated = { ...prev };
      delete updated[paramId];
      return updated;
    });
    updateInput(paramId, 'value', newValue);
  };

  const handleRun = async () => {
    if (toolUploads.activeBatches > 0) {
      setToolUploads((current) => ({ ...current, attemptedRun: true }));
      return;
    }
    // A click on invalid inputs can never become a run, so it is not reported as one.
    if (!validateInputs()) {
      if (!isToolViewRedesignEnabled) setErrorMessage(t(I18N_KEYS.GENERAL.REQUIRED_FIELDS));
      return;
    }

    // The BI trigger is the click, so this precedes the unknown-cost confirmation.
    track(
      TOOL_RUN_EVENT,
      {
        owner: isOwner,
        source: isPreviewMode ? 'preview' : 'tool',
        version: runData.recipeVersion,
        number_of_runs: numberOfRuns,
        tool_id: toolId,
        // Absent whenever the cost is unknown: still loading, failed to load, or priced at run time.
        tool_cost: totalRunCost,
        workflow_id: runData.workflowId,
        // The same condition as the button's label; counts editor runs of the workflow, not just this tool.
        initial: !results?.length,
      },
      TrackTypeEnum.BI,
    );

    if (isDynamicCost) {
      return openConfirmationDialog({
        title: t(I18N_KEYS.RECIPE_MAIN.FLOW.UNKNOWN_COST.DIALOG.TITLE),
        message: t(I18N_KEYS.RECIPE_MAIN.FLOW.UNKNOWN_COST.DIALOG.MSG),
        confirmBtnText: t(I18N_KEYS.RECIPE_MAIN.FLOW.UNKNOWN_COST.DIALOG.CONFIRM_BTN_TEXT),
        width: 'md',
        onConfirm: () => void run(numberOfRuns),
      });
    }
    await run(numberOfRuns);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDraggingPanel(true);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingPanel) return;
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      if (isToolViewRedesignEnabled) {
        const availableSidebarWidth = rect.width - MAIN_CONTENT_MIN_WIDTH - PANEL_RESIZER_WIDTH;
        const maxSidebarWidth = Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, availableSidebarWidth));
        const requestedSidebarWidth = e.clientX - rect.left;
        const width = Math.max(SIDEBAR_MIN_WIDTH, Math.min(requestedSidebarWidth, maxSidebarWidth));
        setSidebarWidth(width);
        localStorage.setItem('toolSidebarWidthPx', String(width));
        return;
      }
      const newWidth = ((e.clientX - rect.left) / rect.width) * 100;
      const limited = Math.min(Math.max(newWidth, 20), 80);
      setLeftPanelWidth(`${limited}%`);
      localStorage.setItem('designAppLeftPanelWidth', `${limited}%`);
    };
    const handleMouseUp = () => {
      setIsDraggingPanel(false);
    };
    if (isDraggingPanel) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingPanel, isToolViewRedesignEnabled]);

  const listSelectorOptions = useCallback(
    (id: string) => {
      const values = inputs.find((i) => i.id === id)?.values ?? [];
      return values.map((v) => String(v));
    },
    [inputs],
  );

  const renderFplInput = (input: ViewerInput) => {
    if (!isToolViewRedesignEnabled) return null;

    const node = viewerNodes.find((node) => node.id === input.id);
    const props = {
      id: `tool-input-${input.id}`,
      label: node?.data.name ?? '',
      disabled: isGuest || isRunActive,
      error: validationErrors[input.id],
    };
    const onChange = (value: unknown) => handleChange(input.id, value);
    const type = input.type as NodeType | 'seed';
    const value: unknown = input.value;
    const description = node?.data.description;
    const withLabel = (control: React.ReactNode) => (
      <ToolInputRow label={props.label} required={input.required} disabled={props.disabled} description={description}>
        {control}
      </ToolInputRow>
    );

    switch (type) {
      case NodeType.MultiLora: {
        if (input.loraMode === 'single' || input.loraOptions === undefined) return null;
        const loraValue = value as Partial<ToolLoraValue> | undefined;
        return withLabel(
          <ToolLoraInput
            {...props}
            options={input.loraOptions}
            value={{
              selectedLora: input.disabled ? null : (loraValue?.selectedLora ?? null),
              weight:
                typeof loraValue?.weight === 'number' && Number.isFinite(loraValue.weight) ? loraValue.weight : 0.5,
            }}
            onChange={(next) => {
              onChange(next);
              updateInput(input.id, 'disabled', next.selectedLora === null);
            }}
          />,
        );
      }
      case NodeType.ColorPalette: {
        // Matches the backend description in weavy/core/migrations/sql/node-definitions/20260518_add-color-node.sql.
        const DEFAULT_COLOR_PALETTE_DESCRIPTION =
          'A palette of solid colors, output as a stacked image and a list of hex codes.';
        return (
          <ToolColorPaletteInput
            {...props}
            required={input.required}
            description={description === DEFAULT_COLOR_PALETTE_DESCRIPTION ? undefined : description}
            value={input.value && 'colors' in input.value ? input.value : undefined}
            onChange={onChange}
          />
        );
      }
      case NodeType.MediaIterator: {
        const acceptedFileType = node?.data.acceptedFileType ?? 'unknown';
        const descriptionKey =
          acceptedFileType === 'video'
            ? I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.ITERATOR_MENU_ITEMS.VIDEO_ITERATOR.DESCRIPTION
            : I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.ITERATOR_MENU_ITEMS.IMAGE_ITERATOR.DESCRIPTION;
        return (
          <ToolMediaIterator
            {...props}
            required={input.required}
            description={description === t(descriptionKey) ? undefined : description}
            value={(input as MediaIteratorInput).value}
            onChange={onChange}
            acceptedFileType={acceptedFileType}
            setIsUploading={handleUploadBatchChange}
          />
        );
      }
      case NodeType.Import:
        return withLabel(
          <FileUploader
            id={input.id}
            value={(value as { file?: Partial<MediaAsset> } | undefined)?.file}
            onUpload={(file) => onChange({ file })}
            onUploadStart={() => handleUploadBatchChange(true)}
            onUploadEnd={() => handleUploadBatchChange(false)}
            disabled={props.disabled}
            isLoading={false}
            error={!!props.error}
            helperText={props.error}
            runType={RunType.WorkflowExecution}
          />,
        );
      case NodeType.Prompt:
      case NodeType.PromptV3:
        return withLabel(
          <ToolTextInput
            {...props}
            value={(input.value as { prompt?: string })?.prompt ?? ''}
            onChange={(prompt) => onChange({ prompt })}
          />,
        );
      case NodeType.String:
        return withLabel(
          <ToolTextInput
            {...props}
            value={(input.value as { string?: string })?.string ?? ''}
            onChange={(string) => onChange({ string })}
          />,
        );
      case NodeType.Boolean:
        return (
          <ToolBooleanInput
            {...props}
            label={requiredLabel(props.label, input.required)}
            value={value === true}
            onChange={onChange}
            description={description}
          />
        );
      case NodeType.MuxV2:
        if (isTextIterator(input)) {
          const placeholderDescription = t(
            I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.ITERATOR_MENU_ITEMS.TEXT_ITERATOR.DESCRIPTION,
          );

          return (
            <ToolTextIterator
              {...props}
              required={input.required}
              description={description === placeholderDescription ? undefined : description}
              value={input.value as { options: string[] }}
              onChange={onChange}
            />
          );
        }
        return withLabel(
          <ToolDropdownInput
            {...props}
            value={selectorIndex(input)}
            options={input.values ?? []}
            onChange={onChange}
          />,
        );
      case NodeType.Array:
        return withLabel(<ToolArrayInput {...props} value={(value ?? '') as string | string[]} onChange={onChange} />);
      case NodeType.Integer:
        return withLabel(
          <ToolNumberInput
            {...props}
            value={Number(value ?? 0)}
            min={node?.data.min}
            max={node?.data.max}
            onChange={onChange}
          />,
        );
      case 'seed': {
        const seedValue = value as { seed?: string | number; isRandom?: boolean } | undefined;
        return withLabel(
          <ToolSeedInput
            {...props}
            value={{
              seed: seedValue?.seed == null ? undefined : Number(seedValue.seed),
              isRandom: seedValue?.isRandom ?? false,
            }}
            onChange={onChange}
          />,
        );
      }
      default:
        return null;
    }
  };

  const renderInputContent = (input: ViewerInput) => {
    const hasError = !!validationErrors[input.id];
    const validationProps = { error: hasError, helperText: hasError ? validationErrors[input.id] : null };

    const header = (
      inputColor: string,
      renderDescription?: Parameters<typeof DesignAppInputHeader>[0]['renderDescription'],
    ) => {
      return (
        <DesignAppInputHeader
          plainLabel={isToolViewRedesignEnabled}
          inputId={input.id}
          updateNodeData={noop}
          renameNodeDisplayName={noop}
          role={role}
          inputColor={inputColor}
          nodes={viewerNodes}
          setIsEditingInputMetadata={noop}
          mode={mode}
          actions={[]}
          renderDescription={renderDescription}
          required={input.required}
        />
      );
    };

    const fplInput = renderFplInput(input);
    if (fplInput) return fplInput;

    if (isTextIterator(input)) {
      return (
        <FlexCol sx={{ width: '100%' }}>
          {header(color.Yambo_Purple, (props) => (
            <IteratorDescription
              {...props}
              placeholderDescription={t(
                I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.ITERATOR_MENU_ITEMS.TEXT_ITERATOR.DESCRIPTION,
              )}
            />
          ))}
          <DesignAppTextIterator
            id={input.id}
            value={input.value as { options: string[] }}
            onChange={handleChange}
            disabled={isGuest}
            {...validationProps}
            helperText={validationProps.helperText ?? undefined}
          />
        </FlexCol>
      );
    }

    switch (input.type) {
      case NodeType.Prompt:
      case NodeType.PromptV3:
        return (
          <FlexCol sx={{ width: '100%' }}>
            {header(color.Yambo_Green)}
            <DesignAppPrompt
              id={input.id}
              value={(input.value as { prompt?: string })?.prompt}
              onChange={handleChange}
              setIsEditingInputMetadata={noop}
              disabled={isGuest}
              isLoading={false}
              {...validationProps}
            />
          </FlexCol>
        );
      case NodeType.MediaIterator: {
        const node = viewerNodes.find((n) => n.id === input.id);
        const acceptedFileType: FileType = node?.data.acceptedFileType ?? 'image';
        const descriptionKey =
          acceptedFileType === 'video'
            ? I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.ITERATOR_MENU_ITEMS.VIDEO_ITERATOR.DESCRIPTION
            : I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.ITERATOR_MENU_ITEMS.IMAGE_ITERATOR.DESCRIPTION;
        return (
          <FlexCol sx={{ width: '100%' }}>
            {header(color.Yambo_Purple, (props) => (
              <IteratorDescription {...props} placeholderDescription={t(descriptionKey)} />
            ))}
            <DesignAppMediaIterator
              id={input.id}
              value={(input as MediaIteratorInput).value}
              onChange={handleChange}
              acceptedFileType={acceptedFileType}
              disabled={isGuest}
              isLoading={false}
              setIsUploading={handleUploadBatchChange}
              {...validationProps}
            />
          </FlexCol>
        );
      }
      case NodeType.Import:
        return (
          <FlexCol sx={{ width: '100%' }}>
            {header(color.Yambo_Blue)}
            <DesignAppUploadFile
              id={input.id}
              value={input.value}
              onUpload={handleChange}
              isLoading={false}
              setIsUploading={handleUploadBatchChange}
              isGuest={isGuest}
              {...validationProps}
              helperText={validationProps.helperText ?? undefined}
            />
          </FlexCol>
        );
      case NodeType.String:
        return (
          <FlexCol sx={{ width: '100%' }}>
            {header(color.Yambo_Blue)}
            <TextArea
              fullWidth
              size="small"
              minRows={1}
              value={(input.value as { string?: string })?.string}
              disabled={isGuest}
              onChange={(e) => handleChange(input.id, { string: e.target.value })}
              sx={{ background: `${color.Black100}`, mt: 0 }}
              {...validationProps}
            />
          </FlexCol>
        );
      default:
        return (
          <FlexCol sx={{ width: '100%' }}>
            {header(color.Yambo_Green)}
            {renderDesignAppParams({
              node: viewerNodes.find((n) => n.id === input.id),
              param: withSelectorIndex(input),
              handleChange,
              isLoading: false,
              validationProps,
              translate: t,
              disabled: isGuest,
              isFplMigrationEnabled,
              listSelectorOptions,
            })}
          </FlexCol>
        );
    }
  };

  const getButtonTitle = () => {
    if (isGuest) return t(I18N_KEYS.TOOL_VIEW.SIGN_IN_TO_RUN);
    if (isProcessing || isRunningApp) return t(I18N_KEYS.GENERAL.RUNNING);
    return results?.length ? t(I18N_KEYS.TOOL_VIEW.RE_RUN) : t(I18N_KEYS.TOOL_VIEW.RUN);
  };

  const formattedMessage = (() => {
    const error = pollingErrorMessage || errorMessage;
    if (!error) return t(I18N_KEYS.GENERAL.UNKNOWN_ERROR);
    return typeof error === 'string' ? error : JSON.stringify(error);
  })();

  const canvasFill = isFplMigrationEnabled ? cssVars.color.fsCanvasDefaultFill : color.Black40_T;
  const panelFill = isFplMigrationEnabled ? cssVars.color.bg : color.Black92;
  const preview = (
    <Preview
      examples={examples}
      layout={isToolViewRedesignEnabled ? 'masonry' : 'carousel'}
      isLoading={false}
      isProcessing={isProcessing || isRunningApp}
      numberOfLoadingResults={numberOfLoadingResults}
      progress={progress}
      recipeName={toolName}
      results={results}
      setResults={setResults}
      version={String(runData.recipeVersion)}
      resultsUrl={endpoints.results}
    />
  );

  const sidebarStyle = stylex.props(isToolViewRedesignEnabled && panelStyles.sidebar(sidebarWidth));
  const sidebarVisibilityStyle = hideSidebarOnMobile
    ? sidebarVisibilityStyles.desktopOnly
    : sidebarVisibilityStyles.alwaysVisible;

  return (
    <Stack.Col
      data-testid="tool-viewer-container"
      ref={containerRef}
      align="center"
      justify="center"
      padding={isToolViewRedesignEnabled ? 0 : cssVars.spacer.x8px}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        background: canvasFill,
        zIndex: isFplMigrationEnabled ? fplZIndex.window : ZIndex.Panels.RECIPE_OVERLAY_PANELS,
      }}
    >
      <Stack.Row
        align="stretch"
        {...stylex.props(panelStyles.panels, isToolViewRedesignEnabled && panelStyles.redesignedPanels)}
      >
        {/* Hide via CSS to preserve input components' local state. */}
        <div {...stylex.props(sidebarVisibilityStyle)} style={isSidebarOpen ? undefined : { display: 'none' }}>
          <div
            {...sidebarStyle}
            id="tool-viewer-sidebar"
            data-testid="tool-viewer-inputs-controls-container"
            style={{
              ...sidebarStyle.style,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: isToolViewRedesignEnabled ? 0 : cssVars.spacer.x16px,
              width: isToolViewRedesignEnabled ? undefined : leftPanelWidth,
              minWidth: isToolViewRedesignEnabled ? undefined : '20%',
              maxWidth: isToolViewRedesignEnabled ? undefined : '80%',
              position: 'relative',
              ...(isToolViewRedesignEnabled
                ? {
                    overflow: 'hidden',
                    background: cssVars.color.bg,
                    borderRight: `1px solid ${cssVars.color.bordertranslucent}`,
                  }
                : { overflow: 'auto' }),
            }}
          >
            <Stack.Col
              ref={inputsContainerRef}
              data-testid="tool-viewer-inputs-container"
              gap={isToolViewRedesignEnabled ? cssVars.spacer.x16px : 0}
              padding={isToolViewRedesignEnabled ? { top: cssVars.spacer.x16px, bottom: cssVars.spacer.x16px } : 0}
              style={{
                width: '100%',
                overflow: 'auto',
                position: 'relative',
                ...(isToolViewRedesignEnabled
                  ? {
                      background: cssVars.color.bg,
                      flex: 1,
                      minHeight: 0,
                    }
                  : {
                      background: panelFill,
                      height: '100%',
                      border: `1px solid ${color.Dark_Grey}`,
                      borderRadius: 12,
                    }),
              }}
            >
              {exposedInputs.length > 0 ? (
                exposedInputs.map((input) => (
                  <Stack.Col
                    key={`input-${input.id}`}
                    ref={(el: HTMLElement | null) => {
                      if (el) inputRefs.current[input.id] = el;
                    }}
                    padding={
                      isToolViewRedesignEnabled
                        ? { left: cssVars.spacer.x16px, right: cssVars.spacer.x16px }
                        : cssVars.spacer.x16px
                    }
                    style={{ width: '100%', position: 'relative', flexShrink: 0 }}
                  >
                    {renderInputContent(input)}
                  </Stack.Col>
                ))
              ) : (
                <Stack.Col align="center" justify="center" style={{ width: '100%', height: '100%' }}>
                  <img
                    src="/illustrations/no-inputs.png"
                    alt="No inputs"
                    style={{ width: '60%', filter: 'grayscale(100%)', opacity: 0.7 }}
                  />
                  <TextStdMd>{t(I18N_KEYS.SHARED_DESIGN_APP.NO_INPUTS.TITLE)}</TextStdMd>
                  <TextSmRg>{t(I18N_KEYS.SHARED_DESIGN_APP.NO_INPUTS.TIP)}</TextSmRg>
                </Stack.Col>
              )}
            </Stack.Col>

            <Stack.Col
              data-testid="tool-viewer-controls-container"
              justify="space-between"
              gap={isToolViewRedesignEnabled ? cssVars.spacer.x8px : cssVars.spacer.x12px}
              padding={
                isToolViewRedesignEnabled
                  ? {
                      top: cssVars.spacer.x8px,
                      right: cssVars.spacer.x16px,
                      bottom: cssVars.spacer.x12px,
                      left: cssVars.spacer.x16px,
                    }
                  : cssVars.spacer.x16px
              }
              style={{
                width: '100%',
                zIndex: 1,
                ...(isToolViewRedesignEnabled
                  ? {
                      background: cssVars.color.bg,
                      borderTop: `1px solid ${cssVars.color.border}`,
                      flexShrink: 0,
                    }
                  : {
                      background: panelFill,
                      border: `1px solid ${color.Dark_Grey}`,
                      borderRadius: 12,
                    }),
              }}
            >
              <RunCounter
                runs={numberOfRuns}
                setRuns={setNumberOfRuns}
                isProcessing={isToolViewRedesignEnabled ? isRunActive : isProcessing}
              />

              {iteratorSummary.length > 0 && <DesignAppIteratorsSummary iterators={iteratorSummary} />}

              {isToolViewRedesignEnabled || isLoadingCost || runCost || isDynamicCost ? (
                <PriceInfo longTitle cost={totalRunCost} isLoading={isLoadingCost} isDynamicCost={isDynamicCost} />
              ) : null}

              {toolUploads.attemptedRun && <RunErrorBanner message={t(I18N_KEYS.TOOL_VIEW.UPLOADS_PENDING)} />}

              {isToolViewRedesignEnabled && Object.keys(validationErrors).length > 0 && (
                <RunErrorBanner
                  message={t(I18N_KEYS.TOOL_VIEW.REQUIRED_FIELDS, { count: Object.keys(validationErrors).length })}
                />
              )}

              {pollingErrorMessage || errorMessage ? (
                <div data-testid="model-error-container">
                  <RunErrorBanner message={formattedMessage} />
                </div>
              ) : null}

              <Stack.Row
                data-testid="tool-viewer-run-and-params-buttons-container"
                align="stretch"
                justify="space-between"
                gap={0}
                style={{
                  width: '100%',
                  ...(isToolViewRedesignEnabled ? { flexDirection: 'column' } : {}),
                }}
              >
                {isToolViewRedesignEnabled ? (
                  isRunActive ? (
                    <Button
                      variant="secondary"
                      width="fill"
                      onClick={() => {
                        setIsCancelling(true);
                        void cancelRun();
                      }}
                      disabled={!canCancel.current || isCancelling}
                    >
                      {t(I18N_KEYS.GENERAL.CANCEL)}
                    </Button>
                  ) : (
                    <Button
                      onClick={
                        isGuest ? () => navigate('/signin', { state: { from: getSignInReturnPath() } }) : handleRun
                      }
                      aria-label={getButtonTitle()}
                      variant="primary"
                      width="fill"
                    >
                      {getButtonTitle()}
                    </Button>
                  )
                ) : (
                  <CancellableLoadingButton
                    onClick={
                      isGuest ? () => navigate('/signin', { state: { from: getSignInReturnPath() } }) : handleRun
                    }
                    onCancel={cancelRun}
                    isProcessing={isRunActive}
                    title={getButtonTitle()}
                    data={{}}
                    canCancel={canCancel.current}
                    subtitle={undefined}
                    shouldShowEndIcon={undefined}
                  />
                )}
              </Stack.Row>
            </Stack.Col>
          </div>

          <div
            {...stylex.props(isToolViewRedesignEnabled && panelStyles.resizer)}
            data-testid="tool-viewer-panel-resizer"
            onMouseDown={handleMouseDown}
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: isToolViewRedesignEnabled ? undefined : PANEL_RESIZER_WIDTH,
              height: '100%',
              background: 'transparent',
              position: 'relative',
              cursor: 'col-resize',
            }}
          >
            {!isToolViewRedesignEnabled && (
              <div
                style={{
                  width: '2px',
                  left: 'calc(50% - 1px)',
                  height: '20px',
                  background: color.Yambo_Purple,
                  position: 'relative',
                  top: 'calc(45% - 10px)',
                  cursor: 'col-resize',
                  borderRadius: '2px',
                  outline: `1px solid ${color.Yambo_Purple}`,
                }}
              />
            )}
          </div>
        </div>

        {isToolViewRedesignEnabled ? (
          <ToolMainView
            toolName={toolName}
            metadata={metadata}
            isEmpty={results.length === 0 && !isProcessing && !isRunningApp}
            examples={examples}
          >
            {preview}
          </ToolMainView>
        ) : (
          preview
        )}
      </Stack.Row>
    </Stack.Col>
  );
}

export default ToolViewer;
